import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { colors } from '../theme/tokens';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ManageProfiles = () => {
  const { token } = useAuth();
  const showToast = useToast();
  const [tab, setTab] = useState('queue');

  return (
    <div>
      <div style={styles.innerTabs}>
        <button
          style={{ ...styles.innerTab, ...(tab === 'queue' ? styles.innerTabActive : {}) }}
          onClick={() => setTab('queue')}
        >
          Pending queue
        </button>
        <button
          style={{ ...styles.innerTab, ...(tab === 'profiles' ? styles.innerTabActive : {}) }}
          onClick={() => setTab('profiles')}
        >
          Profiles
        </button>
        <button
          style={{ ...styles.innerTab, ...(tab === 'new' ? styles.innerTabActive : {}) }}
          onClick={() => setTab('new')}
        >
          New profile
        </button>
      </div>

      {tab === 'queue' && <PendingQueue token={token} showToast={showToast} />}
      {tab === 'profiles' && <ProfilesAdmin token={token} showToast={showToast} />}
      {tab === 'new' && <NewProfile token={token} showToast={showToast} />}
    </div>
  );
};

// ----- Pending approval queue -----
const PendingQueue = ({ token, showToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/profiles/admin/pending-references`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (item, kind, reason) => {
    try {
      const res = await fetch(`${API_URL}/api/profiles/${item.profileId}/references/${item.referenceId}/${kind}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: kind === 'reject' ? JSON.stringify({ reason }) : undefined
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || `${kind} failed`, 'error');
      } else {
        showToast(`${kind === 'approve' ? 'Approved' : 'Rejected'}: ${item.profileCode}${data.hasFeatures ? ' (features computed)' : ''}`, 'success');
        load();
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  const recomputeMissing = async () => {
    if (!window.confirm('Recompute features for every approved reference that\'s missing them? This may take a while for large catalogues.')) return;
    try {
      const res = await fetch(`${API_URL}/api/profiles/admin/recompute-missing`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) showToast(data.error || 'Recompute failed', 'error');
      else showToast(`Recompute done: ${data.done} updated, ${data.failed} failed`, 'success');
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Pending references ({items.length})</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={styles.smallBtn} onClick={recomputeMissing}>⚙ Recompute missing features</button>
          <button style={styles.smallBtn} onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {loading && <div style={{ color: colors.textMuted }}>Loading…</div>}
      {!loading && items.length === 0 && <div style={{ color: colors.textMuted }}>Nothing pending. Nice.</div>}

      <div style={styles.grid}>
        {items.map(item => (
          <div key={item.referenceId} style={styles.queueCard}>
            <AuthThumb
              src={`${API_URL}/api/profiles/${item.profileId}/references/${item.referenceId}/image`}
              token={token}
            />
            <div style={styles.qInfo}>
              <div style={styles.code}>{item.profileCode}</div>
              <div style={{ fontSize: 13, color: colors.text }}>{item.profileName}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>
                {item.source} · {item.addedBy?.name || 'unknown'} · {new Date(item.createdAt).toLocaleString()}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button style={styles.approveBtn} onClick={() => act(item, 'approve')}>
                  ✓ Approve
                </button>
                <button
                  style={styles.rejectBtn}
                  onClick={() => {
                    const reason = window.prompt('Rejection reason (optional)') || '';
                    act(item, 'reject', reason);
                  }}
                >
                  ✕ Reject
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ----- Profile admin list -----
const ProfilesAdmin = ({ token, showToast }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('approved'); // 'approved' | 'all'
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (q) params.set('q', q);
      if (filter === 'approved') params.set('hasApproved', 'true');
      const res = await fetch(`${API_URL}/api/profiles?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [token, q, filter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setSelectedId(id);
    try {
      const res = await fetch(`${API_URL}/api/profiles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDetail(data);
    } catch (e) { console.error(e); }
  };

  const deleteRef = async (refId) => {
    if (!window.confirm('Delete this reference image?')) return;
    try {
      const res = await fetch(`${API_URL}/api/profiles/${selectedId}/references/${refId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { showToast('Deleted', 'success'); openDetail(selectedId); }
      else showToast('Failed to delete', 'error');
    } catch (e) { showToast('Network error', 'error'); }
  };

  return (
    <div className="profile-admin-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 12 }}>
      <div>
        <div style={styles.filterRow}>
          <button
            type="button"
            style={{ ...styles.filterChip, ...(filter === 'approved' ? styles.filterChipActive : {}) }}
            onClick={() => setFilter('approved')}
          >
            ✓ With approved images
          </button>
          <button
            type="button"
            style={{ ...styles.filterChip, ...(filter === 'all' ? styles.filterChipActive : {}) }}
            onClick={() => setFilter('all')}
          >
            All profiles
          </button>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          style={styles.searchInput}
        />
        <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>
          Showing {items.length}{total > items.length ? ` of ${total}` : ''}
          {filter === 'approved' ? ' with approved images' : ''}
        </div>
        <div style={{ maxHeight: 600, overflowY: 'auto', marginTop: 8, border: `1px solid ${colors.glassBorder}`, borderRadius: 8 }}>
          {items.length === 0 && (
            <div style={{ padding: 12, color: colors.textMuted, fontSize: 13 }}>
              No profiles match{filter === 'approved' ? ' (try the "All profiles" filter)' : ''}.
            </div>
          )}
          {items.map(p => (
            <div
              key={p._id}
              onClick={() => openDetail(p._id)}
              style={{
                padding: 10,
                cursor: 'pointer',
                background: selectedId === p._id ? 'rgba(47,182,92,0.16)' : 'transparent',
                borderBottom: `1px solid ${colors.borderSoft}`
              }}
            >
              <div style={{ fontWeight: 700, color: colors.parGreenBright }}>{p.profileCode}</div>
              <div style={{ fontSize: 12, color: colors.text }}>{p.name}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>
                {p.approvedRefCount} approved · {p.pendingRefCount} pending
                {p.noneOfTheseCount > 0 && (
                  <span title="Reps marked this profile as wrong in match results" style={{ marginLeft: 6, color: colors.red, fontWeight: 700 }}>
                    · ⚠ {p.noneOfTheseCount} wrong
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        {!detail && <div style={{ color: colors.textMuted }}>Pick a profile from the list.</div>}
        {detail && (
          <div style={{ color: colors.text }}>
            <h3 style={{ margin: '0 0 8px', color: colors.text }}>{detail.profileCode} — {detail.name}</h3>
            {detail.category && <div>Category: {detail.category}</div>}
            {detail.description && <div style={{ color: colors.textMuted }}>{detail.description}</div>}

            <h4 style={{ marginTop: 16 }}>Reference images ({detail.referenceImages.length})</h4>
            <div style={styles.grid}>
              {detail.referenceImages.map(r => (
                <div key={r._id} style={styles.queueCard}>
                  <AuthThumb
                    src={`${API_URL}/api/profiles/${detail._id}/references/${r._id}/image`}
                    token={token}
                  />
                  <div style={styles.qInfo}>
                    <div style={{ fontSize: 12 }}>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: 4,
                        color: 'white',
                        background:
                          r.status === 'approved' ? '#2fb65c' :
                          r.status === 'pending' ? '#f39c12' : '#999'
                      }}>{r.status}</span>
                      {' '}{r.source}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>
                      {r.addedBy?.name || 'unknown'}
                    </div>
                    <div style={{ fontSize: 11, color: r.hasEmbedding ? colors.parGreenBright : colors.textFaint }}>
                      {r.hasFeatures ? '✓ features' : 'no features'}
                      {r.hasEmbedding ? ' · ✓ embedding' : ''}
                    </div>
                    <button style={{ ...styles.rejectBtn, marginTop: 6 }} onClick={() => deleteRef(r._id)}>
                      🗑 Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ----- New profile form -----
const NewProfile = ({ token, showToast }) => {
  const [form, setForm] = useState({
    profileCode: '',
    name: '',
    category: '',
    description: '',
    width: '',
    height: '',
    wallThickness: ''
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          profileCode: form.profileCode,
          name: form.name,
          category: form.category || undefined,
          description: form.description || undefined,
          dimensions: {
            width: form.width ? Number(form.width) : undefined,
            height: form.height ? Number(form.height) : undefined,
            wallThickness: form.wallThickness ? Number(form.wallThickness) : undefined
          }
        })
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Create failed', 'error');
      } else {
        showToast(`Created ${data.profileCode}`, 'success');
        setForm({ profileCode: '', name: '', category: '', description: '', width: '', height: '', wallThickness: '' });
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally { setBusy(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} style={{ maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h3 style={{ margin: 0 }}>New extrusion profile</h3>
      <Field label="Profile code (e.g. EP-1024)" required>
        <input style={styles.input} value={form.profileCode} onChange={upd('profileCode')} required />
      </Field>
      <Field label="Name" required>
        <input style={styles.input} value={form.name} onChange={upd('name')} required />
      </Field>
      <Field label="Category">
        <input style={styles.input} value={form.category} onChange={upd('category')} />
      </Field>
      <Field label="Description">
        <textarea style={{ ...styles.input, minHeight: 60 }} value={form.description} onChange={upd('description')} />
      </Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <Field label="Width (mm)">
          <input style={styles.input} value={form.width} onChange={upd('width')} type="number" />
        </Field>
        <Field label="Height (mm)">
          <input style={styles.input} value={form.height} onChange={upd('height')} type="number" />
        </Field>
        <Field label="Wall (mm)">
          <input style={styles.input} value={form.wallThickness} onChange={upd('wallThickness')} type="number" />
        </Field>
      </div>
      <button disabled={busy} style={{ ...styles.smallBtn, padding: '12px', background: '#2fb65c', color: 'white', border: 'none' }}>
        {busy ? 'Saving…' : 'Create profile'}
      </button>
    </form>
  );
};

const Field = ({ label, required, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 4 }}>
    <span style={{ fontSize: 12, color: colors.textMuted }}>{label}{required && ' *'}</span>
    {children}
  </label>
);

// Tiny auth-protected thumbnail
class AuthThumb extends React.Component {
  state = { url: null };
  async componentDidMount() {
    try {
      const r = await fetch(this.props.src, { headers: { Authorization: `Bearer ${this.props.token}` } });
      if (!r.ok) return;
      const b = await r.blob();
      this.setState({ url: URL.createObjectURL(b) });
    } catch (e) { /* noop */ }
  }
  componentWillUnmount() { if (this.state.url) URL.revokeObjectURL(this.state.url); }
  render() {
    return (
      <div style={{ width: 80, height: 80, background: colors.surfaceDeep, borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {this.state.url
          ? <img src={this.state.url} alt="ref" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <span style={{ fontSize: 11, color: colors.textFaint }}>…</span>}
      </div>
    );
  }
}

const styles = {
  innerTabs: { display: 'flex', gap: 6, marginBottom: 12 },
  innerTab: {
    padding: '6px 12px', border: `1px solid ${colors.border}`, background: colors.surfaceDeep,
    borderRadius: 16, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: colors.textMuted
  },
  innerTabActive: { background: 'rgba(47,182,92,0.20)', color: colors.parGreenBright, border: '1px solid rgba(47,182,92,0.55)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 },
  queueCard: {
    display: 'flex', gap: 10, padding: 10, background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
  },
  qInfo: { flex: 1, minWidth: 0 },
  code: { color: colors.parGreenBright, fontWeight: 700 },
  smallBtn: {
    padding: '6px 10px', background: colors.surface, border: `1px solid ${colors.border}`,
    borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12, color: colors.text
  },
  approveBtn: {
    padding: '6px 10px', background: '#2fb65c', color: 'white', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12, flex: 1
  },
  rejectBtn: {
    padding: '6px 10px', background: colors.surface, color: colors.textMuted, border: `1px solid ${colors.border}`,
    borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12, flex: 1
  },
  searchInput: { width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, background: colors.inputBg, color: colors.text, boxSizing: 'border-box' },
  filterRow: { display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' },
  filterChip: {
    padding: '6px 10px', background: colors.surfaceDeep, border: `1px solid ${colors.border}`,
    borderRadius: 14, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: colors.textMuted
  },
  filterChipActive: { background: 'rgba(47,182,92,0.20)', color: colors.parGreenBright, border: '1px solid rgba(47,182,92,0.55)' },
  input: { padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, width: '100%', boxSizing: 'border-box', background: colors.inputBg, color: colors.text }
};

export default ManageProfiles;
